/**
 * Maintenance Analysis Service
 * Service for fetching GenAI maintenance analysis
 */

import { apiClient } from '../api';
import { 
  DateRange, 
  MaintenanceAnalysisRequest, 
  MaintenanceAnalysisResponse 
} from './types';

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000;

// Cache structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// In-memory cache
const analysisCache: Record<string, CacheEntry<MaintenanceAnalysisResponse>> = {};

/**
 * Generates a cache key for analysis
 */
const getCacheKey = (site: string, equipment: string, dateRange: DateRange, woIds: string[]): string => {
  const sortedWoIds = [...woIds].sort().join(',');
  return `analysis_${site}_${equipment}_${dateRange.startDate}_${dateRange.endDate}_${sortedWoIds}`;
};

/**
 * Checks if a cache entry is still valid
 */
const isCacheValid = (entry: CacheEntry<any>): boolean => {
  return Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * Maintenance Analysis Service with methods for fetching GenAI analysis
 */
export const maintenanceAnalysisService = {
  /**
   * Fetches maintenance analysis for specific equipment and work orders
   */
  getMaintenanceAnalysis: async (
    site: string,
    equipment: string,
    dateRange: DateRange,
    workOrderIds: string[]
  ): Promise<MaintenanceAnalysisResponse> => {
    
    // Validate input
    if (!site || !equipment || !dateRange || !workOrderIds || workOrderIds.length === 0) {
      throw new Error('Invalid parameters: site, equipment, dateRange, and workOrderIds are required');
    }

    const cacheKey = getCacheKey(site, equipment, dateRange, workOrderIds);
    
    // Check cache first
    const cachedData = analysisCache[cacheKey];
    if (cachedData && isCacheValid(cachedData)) {
      return cachedData.data;
    }
    
    // Prepare request body
    const requestBody: MaintenanceAnalysisRequest = {
      site,
      equipment,
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
      wo_ids: workOrderIds
    };
    
    // Fetch from API
    const response = await apiClient.post<MaintenanceAnalysisResponse>(
      '/maintenance/analysis',
      requestBody
    );
    
    // Update cache
    analysisCache[cacheKey] = {
      data: response,
      timestamp: Date.now()
    };
    
    return response;
  },
  
  /**
   * Clears the analysis cache for a specific equipment
   * or the entire cache if no parameters are provided
   */
  clearCache: (site?: string, equipment?: string): void => {
    if (site && equipment) {
      // Clear cache for specific equipment
      Object.keys(analysisCache).forEach(key => {
        if (key.startsWith(`analysis_${site}_${equipment}`)) {
          delete analysisCache[key];
        }
      });
    } else if (site) {
      // Clear cache for specific site
      Object.keys(analysisCache).forEach(key => {
        if (key.startsWith(`analysis_${site}`)) {
          delete analysisCache[key];
        }
      });
    } else {
      // Clear all cache
      Object.keys(analysisCache).forEach(key => {
        delete analysisCache[key];
      });
    }
  }
};
